import React, { useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import Text from '@components/Text'
import Heading from '@components/Heading'
import { TEXT_SIZES } from '@helpers/constants'
import Image from '@components/Image'
import List from '@components/List'
import Quiz from '@blocks/Quiz/view'
import DragWordToImage from '@blocks/DragWordToImage/view'
import DragImageToCategory from '@blocks/DragImageToCategory/view'
import Embed from '@components/Embed'
import WordExplanation from '@blocks/WordExplanation/view'
import Table from '@components/Table'
import InstructionExercise from '@blocks/InstructionExercise/view'
import EditorDivider from '@blocks/Divider/view'
import AttachmentTool from '@blocks/AttachmentTool/view'

const BlockRenderer = (props) => {
    const { content = '{}' } = props
    var contentJson
    try {
        contentJson = typeof content === 'string' ? JSON.parse(content) : content
    } catch (error) {
        console.error('🚀 ~ BlockRenderer/index.js ~ line 24 ~ content is not valid json: \n', content)
    }
    const [openPanel, setOpenPanel] = useState(undefined)

    const container = classNames('my-0 mx-auto max-w-[700px] break-words')

    return (
        <div className={container}>
            {contentJson?.blocks &&
                contentJson.blocks.map((block, index) => {
                    // console.log('🚀 ~ block', block)
                    if (!block.id) {
                        block.id = 'temp-' + index
                    }

                    if (block.type === 'header') {
                        const {
                            data: { level, text },
                        } = block
                        const headingLevel = `h${level}`
                        // @TODO, match right heading level here
                        const headingSizes = {
                            1: TEXT_SIZES.XXXLARGE,
                            2: TEXT_SIZES.XXLARGE,
                            3: TEXT_SIZES.XLARGE,
                            4: TEXT_SIZES.LARGE,
                            5: TEXT_SIZES.XSMALL,
                            6: TEXT_SIZES.XXSMALL,
                        }
                        const headingSize = headingSizes[level]
                        return (
                            <div key={block.id}>
                                <Heading
                                    headingLevel={headingLevel}
                                    size={headingSize}
                                >
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html: text,
                                        }}
                                    ></span>
                                </Heading>
                            </div>
                        )
                    }

                    if (block.type === 'paragraph') {
                        const {
                            data: { text },
                        } = block

                        return (
                            <div key={block.id}>
                                <Text>
                                    <span
                                        className={'blockRenderer-text'}
                                        dangerouslySetInnerHTML={{
                                            __html: text,
                                        }}
                                    ></span>
                                </Text>
                            </div>
                        )
                    }

                    if (block.type === 'image') {
                        const {
                            data: {
                                caption,
                                file: { url },
                                //@TODO, see how to/if we should use these settings below
                                // stretched,
                                // withBackground,
                                // withBorder,
                                withLightbox
                            },
                        } = block
                        return (
                            <div key={block.id}>
                                <Image
                                    imageUrl={url}
                                    altText={url}
                                    caption={caption}
                                    withLightbox={withLightbox ? true : false}
                                />
                            </div>
                        )
                    }

                    if (block.type === 'list') {
                        const {
                            data: { style, items },
                        } = block
                        return (
                            <div key={block.id} className={'blockRenderer-list'}>
                                {style === 'ordered' ? (
                                    <List ordered items={items} />
                                ) : (
                                    <List unordered items={items} />
                                )}
                            </div>
                        )
                    }

                    // Custom blocks

                    if (block.type === 'divider') {
                        return <EditorDivider key={block.id} {...block.data} />
                    }

                    if (block.type === 'quiz') {
                        return (
                            <div key={block.id}>
                                <Quiz {...block.data}
                                    showPanel={openPanel === block.id || openPanel === 'temp-' + index}
                                    onShowPanel={() => setOpenPanel(block.id)}
                                    onClosePanel={() => setOpenPanel(undefined)}
                                />
                            </div>
                        )
                    }

                    if (block.type === 'dragWordToImage') {
                        return (
                            <div key={block.id}>
                                <DragWordToImage {...block.data}
                                    showPanel={openPanel === block.id || openPanel === 'temp-' + index}
                                    onShowPanel={() => setOpenPanel(block.id)}
                                    onClosePanel={() => setOpenPanel(undefined)}
                                />
                            </div>
                        )
                    }

                    if (block.type === 'dragImageToCategory') {
                        return (
                            <div key={block.id}>
                                <DragImageToCategory {...block.data}
                                    showPanel={openPanel === block.id || openPanel === 'temp-' + index}
                                    onShowPanel={() => setOpenPanel(block.id)}
                                    onClosePanel={() => setOpenPanel(undefined)}
                                />
                            </div>
                        )
                    }

                    if (block.type === 'instructionExercise') {
                        return (
                            <div key={block.id}>
                                <InstructionExercise {...block.data}
                                    showPanel={openPanel === block.id || openPanel === 'temp-' + index}
                                    onShowPanel={() => setOpenPanel(block.id)}
                                    onClosePanel={() => setOpenPanel(undefined)}
                                />
                            </div>
                        )
                    }

                    if (block.type === 'attaches') {
                        return (
                            <div key={block.id}>
                                <AttachmentTool {...block.data} />
                            </div>
                        )
                    }

                    if (block.type === 'embed') {
                        const {
                            data: { embed, caption },
                        } = block
                        return (
                            <div key={block.id}>
                                <Embed embedUrl={embed} caption={caption} />
                            </div>
                        )
                    }

                    if (block.type === 'wordExplanation') {
                        return (
                            <div key={block.id}>
                                <WordExplanation id={block.id} {...block.data} />
                            </div>
                        )
                    }

                    if (block.type === 'table') {
                        return (
                            <div key={block.id} className={'blockRenderer-table'}>
                                <Table {...block.data} />
                            </div>
                        )
                    }

                    return null
                })}
        </div>
    )
}

BlockRenderer.propTypes = {
    content: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
}

export default BlockRenderer
